import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Zap, GitBranch } from "lucide-react"

export function NextShadcnOverviewPage() {
  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Zap className="h-10 w-10 text-primary" />
          <h1 className="text-5xl font-bold">Next.js + Shadcn UI Template</h1>
        </div>
        <p className="text-xl text-muted-foreground max-w-4xl leading-relaxed">
          Production-ready Next.js 14 template with App Router, Shadcn UI, TypeScript, and clean 
          architecture. Perfect for building modern, SEO-friendly web applications with server-side 
          rendering and beautiful, accessible components.
        </p>
        <div className="flex gap-3 pt-2">
          <Badge variant="secondary" className="text-base py-1.5 px-3">Next.js 14</Badge>
          <Badge variant="secondary" className="text-base py-1.5 px-3">App Router</Badge>
          <Badge variant="secondary" className="text-base py-1.5 px-3">Shadcn UI</Badge>
          <Badge variant="secondary" className="text-base py-1.5 px-3">TypeScript</Badge>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <QuickLinkCard
          title="Quick Start"
          description="Get the template running in minutes with our step-by-step guide"
          href="/nextjs/shadcn/quick-start"
        />
        <QuickLinkCard
          title="Features"
          description="Explore all the built-in features and capabilities"
          href="/nextjs/shadcn/features"
        />
        <QuickLinkCard
          title="Architecture"
          description="Understand the Next.js App Router and code organization"
          href="/nextjs/shadcn/architecture"
        />
        <QuickLinkCard
          title="Components"
          description="Discover the UI components and patterns"
          href="/nextjs/shadcn/components"
        />
        <QuickLinkCard
          title="Server & Client"
          description="Learn about Server and Client Components"
          href="/nextjs/shadcn/server-client"
        />
        <QuickLinkCard
          title="State Management"
          description="Explore Zustand stores and custom hooks"
          href="/nextjs/shadcn/state"
        />
      </div>

      {/* What's Included */}
      <div className="bg-muted/30 rounded-xl p-8 space-y-6">
        <h2 className="text-3xl font-bold">What's Included</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Next.js & UI</h3>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Next.js 14 with App Router and Server Components</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Shadcn UI component library (accessible & customizable)</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Dark/Light theme with system preference support</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Server-side rendering and Static Site Generation</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Optimized images and fonts with next/image and next/font</span>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Architecture & Performance</h3>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>App Router with layouts and loading states</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Type-safe API routes with Route Handlers</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Zustand for client-side state management</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>SEO-friendly with metadata API</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>TypeScript types and constants throughout</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Getting Started CTA */}
      <div className="bg-card border rounded-xl p-10 text-center space-y-6">
        <h2 className="text-3xl font-bold">Ready to Get Started?</h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Follow our quick start guide to have the template running in just a few minutes, 
          or dive deep into the documentation to understand every aspect of the template.
        </p>
        <div className="flex gap-4 justify-center pt-4">
          <Button asChild size="lg" className="text-base">
            <Link to="/nextjs/shadcn/quick-start">
              Get Started
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="text-base">
            <a
              href="https://github.com/yourusername/next-shadcn-template"
              target="_blank"
              rel="noopener noreferrer"
            >
              <GitBranch className="mr-2 h-5 w-5" />
              View on GitHub
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}

function QuickLinkCard({ 
  title, 
  description, 
  href 
}: { 
  title: string
  description: string
  href: string
}) {
  return (
    <Link 
      to={href}
      className="group block p-6 bg-card border rounded-xl hover:border-primary/50 transition-all hover:shadow-md"
    >
      <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
        {title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {description}
      </p>
    </Link>
  )
}
